import { OptionInput, PhoneInput, TextInput } from "@brexhq/component-library";
import * as React from "react";
import { FormState } from "react-hook-form";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import {
  employeeCountOptions,
  FormSubmissionState,
  Step,
  FieldValues,
} from "@brexhq/next/src/components/modules/FormMultistep/types";
import { BodyM, TitleS } from "@brexhq/shared/components/Typography/Components";

import {
  Actions,
  bodyMargin,
  ErrorBox,
  titleMargin,
} from "../FormMultistep.elements";
import { DynamicLeadFormController } from "../dynamicLeadFormController";

import { dynamicSecondStep } from "./dynamicSecondStep";

const DynamicBasics: React.FC<{ ctrl: DynamicLeadFormController }> = ({
  ctrl,
}) => {
  const {
    fields,
    formSubmissionState,
    next,
    customFormTitle,
    showCustomLeftContent,
    customSubmitButtonLabel,
  } = ctrl;
  return (
    <>
      <div>
        <TitleS margin={titleMargin}>
          {customFormTitle || "Let’s get your information."}
        </TitleS>
        {!showCustomLeftContent && (
          <BodyM margin={bodyMargin}>
            To help us connect you with the right sales team member or resource,
            please answer a few quick questions.
          </BodyM>
        )}
      </div>
      <Container
        width="100%"
        display="flex"
        direction={{
          LRG: "row",
          SML: "column",
        }}
        gap="sml"
      >
        <div style={{ flex: 1 }}>
          <TextInput
            inputMode="text"
            size="large"
            autoComplete="given-name"
            {...fields.firstName}
          />
        </div>
        <div style={{ flex: 1 }}>
          <TextInput
            inputMode="text"
            size="large"
            autoComplete="family-name"
            {...fields.lastName}
          />
        </div>
      </Container>
      <TextInput
        inputMode="email"
        size="large"
        autoComplete="email"
        {...fields.workEmail}
      />
      <PhoneInput size="large" autoComplete="tel" {...fields.mobileNumber} />
      <TextInput
        size="large"
        autoComplete="organization"
        {...fields.companyName}
      />
      <OptionInput
        {...fields.employeeCount}
        size="large"
        options={employeeCountOptions}
      />
      <TextInput size="large" {...fields.howDidYouHearAboutUs} />
      {formSubmissionState === FormSubmissionState.SUBMITTED_AND_FAILED && (
        <ErrorBox onSubmit={next} />
      )}
      <Actions ctrl={ctrl} label={customSubmitButtonLabel} />
    </>
  );
};

export const dynamicFirstStep: Step = {
  id: "Dynamic first step",
  view: DynamicBasics,

  /**
   * Checks that the relevant fields don't have validation errors. Note that
   * its necessary to check that the field is dirty as the fact that a field
   * is required isn't enforced until it's been dirtied. Checking that a field
   * is dirty and error free ensures that it was edited at least once and the
   * current value passes validation.
   */
  isValid: ({ errors, dirtyFields }: FormState<FieldValues>): boolean => {
    const noErrors = [
      errors.firstName,
      errors.lastName,
      errors.workEmail,
      errors.mobileNumber,
      errors.companyName,
      errors.employeeCount,
      errors.howDidYouHearAboutUs,
    ].every((error) => error === undefined);

    const allDirty = [
      dirtyFields.firstName,
      dirtyFields.lastName,
      dirtyFields.workEmail,
      dirtyFields.mobileNumber,
      dirtyFields.companyName,
      dirtyFields.employeeCount,
      dirtyFields.howDidYouHearAboutUs,
    ].every((field) => field);

    return allDirty && noErrors;
  },
  hasPrevStep: () => false,
  next: async ({ createOrUpdateLead, setStep, finishWithUpdate, fields }) => {
    const employeeCount = fields.employeeCount.value;

    await createOrUpdateLead();

    if (
      employeeCount === employeeCountOptions[3].value ||
      employeeCount === employeeCountOptions[4].value ||
      employeeCount === employeeCountOptions[5].value ||
      employeeCount === employeeCountOptions[6].value
    ) {
      return await finishWithUpdate();
    }

    return setStep(dynamicSecondStep);
  },
  back: () => {
    return null;
  },
};
